import { z } from 'zod';
import {
  acquiringDocumentTypes,
  documentPeriod,
  documentPeriodPreset,
  isoDate,
  maxDocumentCounterparties,
} from '@qronstat/shared';
import type { DocumentPeriod } from '@qronstat/shared';

export const periodKinds = [
  'prevMonth',
  'thisMonth',
  'prevQuarter',
  'thisQuarter',
  'month',
  'quarter',
  'custom',
] as const;
export type PeriodKind = (typeof periodKinds)[number];

export const documentsSearch = z.object({
  t: z.enum(acquiringDocumentTypes).optional().catch(undefined),
  cp: z.array(z.string().uuid()).max(maxDocumentCounterparties).optional().catch(undefined),
  shop: z.string().uuid().optional().catch(undefined),
  pk: z.enum(periodKinds).optional().catch(undefined),
  from: isoDate.optional().catch(undefined),
  to: isoDate.optional().catch(undefined),
});
export type DocumentsSearch = z.infer<typeof documentsSearch>;

export function mskToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export function resolveDocumentPeriod(search: DocumentsSearch, today: string): DocumentPeriod | null {
  const kind = search.pk ?? 'prevMonth';
  if (kind === 'month' || kind === 'quarter' || kind === 'custom') {
    const parsed = documentPeriod.safeParse({ from: search.from, to: search.to });
    return parsed.success ? parsed.data : null;
  }
  return documentPeriodPreset(kind, today);
}

export function recentMonths(today: string, count = 36): string[] {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7)) - 1;
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(Date.UTC(year, month - i, 1));
    return d.toISOString().slice(0, 7);
  }).filter((m) => m >= '2020-01');
}

export function recentQuarters(today: string, count = 12): { year: number; quarter: number }[] {
  const year = Number(today.slice(0, 4));
  const quarter = Math.floor((Number(today.slice(5, 7)) - 1) / 3) + 1;
  return Array.from({ length: count }, (_, i) => {
    const index = year * 4 + (quarter - 1) - i;
    return { year: Math.floor(index / 4), quarter: (index % 4) + 1 };
  }).filter((q) => q.year >= 2020);
}

export function documentsHref(target: { counterpartyIds?: string[]; shopId?: string }): {
  to: '/payments/documents';
  search: Record<string, unknown>;
} {
  return {
    to: '/payments/documents',
    search: {
      ...(target.counterpartyIds?.length ? { cp: target.counterpartyIds } : {}),
      ...(target.shopId ? { shop: target.shopId } : {}),
    },
  };
}
